<template>
	<transition name="drawer">
		<div
			v-if="show"
			class="base-drawer"
			:data-test-id="dataTestId"
			@mousedown.self="handleBackdrop"
		>
			<div
				:class="['base-drawer__panel', `base-drawer__panel--${size}`]"
				role="dialog"
				aria-modal="true"
			>
				<slot name="header">
					<ModalHeaderWrapper
						:title="title"
						:hide-close-button="hideCloseButton"
						@onClose="close"
					>
						<template #actions>
							<slot name="header-actions"></slot>
						</template>
					</ModalHeaderWrapper>
				</slot>

				<div class="base-drawer__body">
					<slot></slot>
				</div>

				<div v-if="$slots.footer" class="base-drawer__footer">
					<slot name="footer"></slot>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import ModalHeaderWrapper from '@/components/ModalHeaderWrapper.vue';

const BODY_OPEN_CLASS = 'base-drawer-open';

export default {
	name: 'BaseDrawer',
	components: {
		ModalHeaderWrapper,
	},
	props: {
		show: {
			type: Boolean,
			default: false,
		},
		title: {
			type: String,
			default: null,
		},
		size: {
			type: String,
			default: 'md',
			validator: (val) => ['sm', 'md', 'lg', 'xl'].includes(val),
		},
		closeOnBackdrop: {
			type: Boolean,
			default: false,
		},
		closeOnEsc: {
			type: Boolean,
			default: true,
		},
		hideCloseButton: {
			type: Boolean,
			default: false,
		},
		dataTestId: {
			type: String,
			default: 'base-drawer',
		},
	},
	watch: {
		show: {
			immediate: true,
			handler(value) {
				this.toggleBodyScroll(value);
			},
		},
	},
	mounted() {
		document.addEventListener('keydown', this.handleKeydown);
	},
	beforeDestroy() {
		document.removeEventListener('keydown', this.handleKeydown);
		this.toggleBodyScroll(false);
	},
	methods: {
		close() {
			this.$emit('update:show', false);
			this.$emit('close');
		},
		handleBackdrop() {
			if (this.closeOnBackdrop) {
				this.close();
			}
		},
		handleKeydown(event) {
			if (this.show && this.closeOnEsc && event.key === 'Escape') {
				this.close();
			}
		},
		toggleBodyScroll(isOpen) {
			if (typeof document === 'undefined') {
				return;
			}
			document.body.classList.toggle(BODY_OPEN_CLASS, isOpen);
		},
	},
};
</script>

<style lang="scss" scoped>
.base-drawer {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: $z-index-modal;

	display: flex;
	justify-content: flex-end;

	background-color: rgba(0, 0, 0, 0.5);

	&__panel {
		position: relative;

		display: flex;
		flex-direction: column;

		width: 100%;
		height: 100vh;

		background-color: $color-white;
		box-shadow: rem(-2) 0 rem(8) rgba(0, 0, 0, 0.15);

		@include sm {
			&--sm {
				max-width: rem(380);
			}

			&--md {
				max-width: rem(480);
			}

			&--lg {
				max-width: rem(640);
			}

			&--xl {
				max-width: rem(820);
			}
		}
	}

	&__body {
		flex: 1 1 auto;
		overflow-y: auto;
		padding: rem(16) rem(24);
	}

	&__footer {
		flex: 0 0 auto;
		display: flex;
		align-items: center;

		padding: rem(12) rem(24);
		border-top: rem(1) solid $color-gray-300;
	}
}

// slide-in from the right + backdrop fade
.drawer-enter-active,
.drawer-leave-active {
	transition: opacity 0.25s ease;

	.base-drawer__panel {
		transition: transform 0.25s ease;
	}
}

.drawer-enter,
.drawer-leave-to {
	opacity: 0;

	.base-drawer__panel {
		transform: translateX(100%);
	}
}
</style>

<style lang="scss">
// global (unscoped): lock background scroll while a drawer is open.
// class is applied to <body> only (see toggleBodyScroll).
.base-drawer-open {
	overflow: hidden;
}
</style>
